/**
 * The one vocabulary for a deploy run: what its status is called, what colour
 * it gets, and the few facts every deployment view asks of a record.
 */
import type { DeploymentRecord } from '@/services/deploymentService';
import type { ECRImage } from '@/types';

export type StatusTone = 'success' | 'danger' | 'warning' | 'info' | 'secondary';

export interface StatusWord {
  label: string;
  tone: StatusTone;
  /** The run is still going. */
  live: boolean;
}

const STATUS_WORDS: Record<string, StatusWord> = {
  running: { label: 'Running', tone: 'warning', live: true },
  retrying: { label: 'Retrying', tone: 'warning', live: true },
  success: { label: 'Succeeded', tone: 'success', live: false },
  failed: { label: 'Failed', tone: 'danger', live: false },
  // A group run where some clients failed and some did not.
  partial: { label: 'Partly failed', tone: 'warning', live: false },
  stopped: { label: 'Stopped', tone: 'secondary', live: false },
  resolved: { label: 'Resolved', tone: 'secondary', live: false },
  // A plan-only run. The pipeline records every finished plan as "no changes", whether or not it
  // found any, so neither status says what the plan found: that is read from the log.
  'plan-no-changes': { label: 'Planned', tone: 'info', live: false },
  'plan-changes': { label: 'Planned', tone: 'info', live: false },
  // Not stored statuses: a client of a group run with no run of its own. While the group
  // runs it is waiting for its turn. Once the group ended, it never started.
  waiting: { label: 'Waiting', tone: 'secondary', live: false },
  'not-started': { label: 'Not started', tone: 'secondary', live: false },
};

export function statusWord(status?: string): StatusWord {
  const key = (status || '').toLowerCase();
  return STATUS_WORDS[key] || { label: status ? status : 'Unknown', tone: 'secondary', live: false };
}

export const statusKey = (record: Pick<DeploymentRecord, 'status'>): string => (record.status || '').toLowerCase();

export const isLive = (record: Pick<DeploymentRecord, 'status'>): boolean => statusWord(record.status).live;

export const isFailed = (record: Pick<DeploymentRecord, 'status'>): boolean => statusKey(record) === 'failed';

/** A deploy stops itself at two hours. A run still marked live after that is dead. */
export const STUCK_AFTER_MS = 2 * 60 * 60 * 1000;

export function isStuck(record: DeploymentRecord, now: number = Date.now()): boolean {
  if (!isLive(record) || !record.startedAt) return false;
  const started = Date.parse(record.startedAt);
  return Number.isFinite(started) && now - started > STUCK_AFTER_MS;
}

/** The summary row of a group run (one image sent to many clients). */
export function isGroupSummary(record: DeploymentRecord): boolean {
  if (record.entityType === 'group') return true;
  if (record.clientName?.startsWith('group#')) return true;
  return record.deploymentId.startsWith('group-') && !record.deploymentId.includes('#');
}

/** One client's run inside a group run. */
export function isGroupMember(record: DeploymentRecord): boolean {
  return Boolean(record.groupRunId) && record.deploymentId.includes('#');
}

export function groupDisplayName(record: DeploymentRecord): string {
  return record.groupName || record.clientName.replace(/^group#/, '') || 'Group run';
}

/** "nathan" from "nathan@arcanum.ai". The full address goes in the tooltip. */
export function shortUser(email?: string): string {
  return email ? email.split('@')[0] : '';
}

export type ImagesByChannel = Record<'numa-deploy' | 'numa-deploy-dev', ECRImage[]>;

export interface ImageDisplay {
  /** The image's given name, or its tag when it has none. */
  label: string;
  tag: string;
  named: boolean;
  dev: boolean;
}

export function imageDisplay(
  record: Pick<DeploymentRecord, 'imageTag' | 'repository'>,
  images?: ImagesByChannel
): ImageDisplay {
  const repository = record.repository || 'numa-deploy';
  const tag = record.imageTag || '';
  const match = images?.[repository]?.find((img) => img.tag === tag);
  return {
    label: match?.customName || tag,
    tag,
    named: Boolean(match?.customName),
    dev: repository === 'numa-deploy-dev',
  };
}

/** Deploy logs are kept for 30 days. */
export const LOG_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

export function isLogExpired(record: DeploymentRecord, now: number = Date.now()): boolean {
  const at = Date.parse(record.endedAt || record.startedAt || '');
  return Number.isFinite(at) && now - at > LOG_RETENTION_MS;
}

/** ECS forgets a stopped task after about an hour, and its console link dies with it. */
export function isEcsLinkAlive(record: DeploymentRecord, now: number = Date.now()): boolean {
  if (!record.ecsTaskArn) return false;
  if (isLive(record)) return true;
  const ended = Date.parse(record.endedAt || '');
  return Number.isFinite(ended) && now - ended < 60 * 60 * 1000;
}

/**
 * The newest run for each client among the rows given (rows are newest first).
 * A failed run that is not its client's newest is old news: a later run took
 * its place.
 */
export function newestRunByClient(rows: DeploymentRecord[]): Map<string, DeploymentRecord> {
  const newest = new Map<string, DeploymentRecord>();
  for (const row of rows) {
    if (isGroupSummary(row) || !row.clientName) continue;
    const current = newest.get(row.clientName);
    if (!current || (row.startedAt || '') > (current.startedAt || '')) newest.set(row.clientName, row);
  }
  return newest;
}

/** The counts a group run shows. `running` and `waiting` need the member rows; the rest are on the summary. */
export interface GroupCounts {
  total: number;
  done: number;
  succeeded: number;
  failed: number;
  running: number;
  waiting: number;
}

export function groupCounts(summary: DeploymentRecord, members?: DeploymentRecord[]): GroupCounts {
  const total = summary.clientsTotal ?? summary.clients?.length ?? 0;
  const succeeded = summary.clientsSucceeded ?? 0;
  const failed = summary.clientsFailed ?? 0;
  const done = Math.max(summary.clientsCompleted ?? 0, succeeded + failed);
  const left = Math.max(0, total - done);
  let running: number;
  if (!isLive(summary)) {
    running = 0;
  } else if (members) {
    // Count clients, not rows: a client run a second time has two rows.
    running = Math.min(left, new Set(members.filter(isLive).map((m) => m.clientName)).size);
  } else {
    // No member rows loaded yet: every client not done is running or about to, up to the run's concurrency.
    running = Math.min(left, summary.maxConcurrency || left);
  }
  return { total, done, succeeded, failed, running, waiting: Math.max(0, left - running) };
}

/** ", after about 10 min" for an error that needs a pause before the second run. Empty when no real wait is needed. */
export function waitPhrase(waitSeconds: number): string {
  if (waitSeconds < 120) return '';
  return `, after about ${Math.round(waitSeconds / 60)} min`;
}

/**
 * A run the pipeline is about to try a second time. Its status is `retrying`
 * until the second task starts, and `retryAt` says when that is. The task and
 * the log on the record are still the first attempt's until then.
 */
export function isAwaitingRetry(record: DeploymentRecord): boolean {
  return statusKey(record) === 'retrying' && Boolean(record.retryAt);
}

/** Seconds until the second attempt starts. 0 once the time has passed. Null when the run is not waiting for one. */
export function secondsToRetry(record: DeploymentRecord, now: number = Date.now()): number | null {
  if (!isAwaitingRetry(record)) return null;
  const at = Date.parse(record.retryAt || '');
  if (!Number.isFinite(at)) return null;
  return Math.max(0, Math.ceil((at - now) / 1000));
}

/** A deploy's AWS access lasts one hour. Past it the run cannot save its state or free its lock. */
export const ACCESS_WARNING_AFTER_MS = 50 * 60 * 1000;

/**
 * When the attempt now running got its AWS access (epoch ms), or null when
 * that is not known. A second attempt gets fresh access when it starts, and
 * the record's own start time is the first attempt's.
 */
export function attemptStartedAt(record: DeploymentRecord): number | null {
  const at = Date.parse(((record.attemptNumber ?? 1) > 1 ? record.retryAt : record.startedAt) || '');
  return Number.isFinite(at) ? at : null;
}

/** A plan-only run that ran to the end. What it found is in its log, not in its status. */
export function isFinishedPlan(record: DeploymentRecord): boolean {
  const status = statusKey(record);
  return status === 'plan-no-changes' || status === 'plan-changes';
}
